import * as React from 'react';
import { toast } from 'sonner';
import { Plus, CalendarOff, Inbox, Filter, UserPlus } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs';
import { useConfirm, usePrompt } from '../../../components/ui/confirm-dialog';
import { DataTable } from '../../../design-system/components/DataTable';
import { EmptyState } from '../../../design-system/components/EmptyState';
import { useApi } from '../../../design-system/useApi';
import { formatDate } from '../../../design-system/format';
import { VISUAL, statusPill, statusDot, type StatusTone } from '../../../design-system/visual';
import { hrApi } from '../api';
import { RequestLeaveDialog } from '../components/RequestLeaveDialog';
import { LeaveTypesPanel } from '../components/LeaveTypesPanel';
import type { LeaveRequest, LeaveStatus, EmployeeWithUser } from '../types';

export function LeavePage() {
  const inboxQ = useApi<{ rows: LeaveRequest[] }>('/api/hr/leave?status=pending');
  const myQ    = useApi<{ rows: LeaveRequest[] }>('/api/hr/leave/me');
  const empQ   = useApi<{ employees: EmployeeWithUser[] }>('/api/hr/employees');
  const confirm = useConfirm();
  const prompt = usePrompt();
  const [open, setOpen] = React.useState(false);
  const [logOpen, setLogOpen] = React.useState(false);

  const canSeeInbox = inboxQ.error == null;   // HR/manager perms (view_all)
  const canSeeOwn = myQ.error == null;
  const employeesById: Record<string, EmployeeWithUser> = {};
  for (const e of empQ.data?.employees ?? []) employeesById[e.id] = e;

  // Filters for the "All requests" view (HR only).
  const [statusFilter, setStatusFilter] = React.useState<LeaveStatus | 'all'>('all');
  const [fromDate, setFromDate] = React.useState('');
  const [toDate, setToDate] = React.useState('');
  const allQs = (() => {
    const p = new URLSearchParams();
    if (statusFilter !== 'all') p.set('status', statusFilter);
    if (fromDate) p.set('from', fromDate);
    if (toDate) p.set('to', toDate);
    return p.toString();
  })();
  const allQ = useApi<{ rows: LeaveRequest[] }>(
    canSeeInbox ? `/api/hr/leave${allQs ? '?' + allQs : ''}` : null,
    [statusFilter, fromDate, toDate],
  );

  async function review(id: string, status: 'approved' | 'denied') {
    const note = (await prompt({ title: `Optional note for ${status}:`, defaultValue: '' })) ?? '';
    try {
      await hrApi.reviewLeave(id, status, note || undefined);
      toast.success(`Marked ${status}`);
      inboxQ.reload(); myQ.reload(); allQ.reload();
    } catch (err: any) { toast.error('Review failed', { description: err.message }); }
  }

  async function cancel(id: string) {
    if (!(await confirm({ title: 'Cancel this request?' }))) return;
    try {
      await hrApi.cancelLeave(id);
      toast.success('Cancelled');
      myQ.reload();
    } catch (err: any) { toast.error('Cancel failed', { description: err.message }); }
  }

  return (
    <div>
      <PageHeader
        eyebrow="Team / Leave"
        title="Time off"
        description="Submit, review, and track leave requests."
        actions={(canSeeOwn || canSeeInbox) && (
          <div className="flex items-center gap-2">
            {canSeeInbox && (
              <Button
                variant="outline"
                onClick={() => setLogOpen(true)}
                className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5"
              >
                <UserPlus className="h-3.5 w-3.5" />
                Log leave
              </Button>
            )}
            {canSeeOwn && (
              <Button
                onClick={() => setOpen(true)}
                className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5"
              >
                <Plus className="h-3.5 w-3.5" />
                Request leave
              </Button>
            )}
          </div>
        )}
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12">
        <Tabs defaultValue="requests">
          <TabsList className="mb-6">
            <TabsTrigger value="requests">Requests</TabsTrigger>
            {canSeeInbox && <TabsTrigger value="types">Leave types</TabsTrigger>}
          </TabsList>

          <TabsContent value="requests" className="space-y-8">
            {canSeeInbox && (
              <section>
                <div className="flex items-baseline justify-between mb-3">
                  <h3 className={VISUAL.dashSection}>Pending review</h3>
                  <span className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground">
                    <Inbox className="h-3.5 w-3.5" />
                    {(inboxQ.data?.rows ?? []).length} awaiting
                  </span>
                </div>
                <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
                  <DataTable<LeaveRequest>
                    loading={inboxQ.loading}
                    rows={inboxQ.data?.rows ?? []}
                    rowKey={(r) => r.id}
                    columns={[
                      { key: 'who', header: 'Employee', cell: (r) => employeesById[r.employee_id]?.name ?? r.employee_id.slice(0, 8) },
                      { key: 'type', header: 'Type', cell: (r) => r.leave_type },
                      { key: 'range', header: 'Range', cell: (r) => `${formatDate(r.start_date)} → ${formatDate(r.end_date)}` },
                      { key: 'reason', header: 'Reason', cell: (r) => r.reason ?? '—', className: 'text-muted-foreground' },
                      { key: 'actions', header: '', cell: (r) => (
                        <div className="flex gap-2 justify-end">
                          <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => review(r.id, 'denied')}>Deny</Button>
                          <Button size="sm" className="rounded-full h-8" onClick={() => review(r.id, 'approved')}>Approve</Button>
                        </div>
                      ) },
                    ]}
                    emptyState={<EmptyState icon={<Inbox />} title="Nothing pending" description="Approved or denied requests appear in the All requests list below." />}
                  />
                </div>
              </section>
            )}

            {canSeeInbox && (
              <section>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <h3 className={VISUAL.dashSection}>All requests</h3>
                  <div className="flex flex-wrap items-center gap-2.5">
                    <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] font-semibold text-muted-foreground/80">
                      <Filter className="h-3.5 w-3.5" /> Filter
                    </span>
                    <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as LeaveStatus | 'all')}>
                      <SelectTrigger className="h-9 rounded-full bg-card/80 border-border/60 px-3.5 text-[12.5px] font-semibold w-auto min-w-[130px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="denied">Denied</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-9 rounded-full bg-card/80 border-border/60 text-[12.5px] w-auto" aria-label="From date" />
                    <span className="text-muted-foreground text-[12px]">→</span>
                    <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-9 rounded-full bg-card/80 border-border/60 text-[12.5px] w-auto" aria-label="To date" />
                    {(statusFilter !== 'all' || fromDate || toDate) && (
                      <Button size="sm" variant="ghost" className="rounded-full h-9" onClick={() => { setStatusFilter('all'); setFromDate(''); setToDate(''); }}>Clear</Button>
                    )}
                  </div>
                </div>
                <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
                  <DataTable<LeaveRequest>
                    loading={allQ.loading}
                    rows={allQ.data?.rows ?? []}
                    rowKey={(r) => r.id}
                    columns={[
                      { key: 'who', header: 'Employee', cell: (r) => employeesById[r.employee_id]?.name ?? r.employee_id.slice(0, 8) },
                      { key: 'type', header: 'Type', cell: (r) => r.leave_type },
                      { key: 'range', header: 'Range', cell: (r) => `${formatDate(r.start_date)} → ${formatDate(r.end_date)}` },
                      { key: 'status', header: 'Status', cell: (r) => statusBadge(r.status) },
                      { key: 'note', header: 'Reviewer note', cell: (r) => r.reviewer_note ?? '—', className: 'text-muted-foreground' },
                    ]}
                    emptyState={<EmptyState icon={<CalendarOff />} title="No matching requests" description="Adjust the status or date filters above." />}
                  />
                </div>
              </section>
            )}

            {canSeeOwn && (
              <section className="space-y-4">
                <h3 className={[VISUAL.dashSection].join(' ')}>My requests</h3>
                <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
                  <DataTable<LeaveRequest>
                    loading={myQ.loading}
                    rows={myQ.data?.rows ?? []}
                    rowKey={(r) => r.id}
                    columns={[
                      { key: 'type', header: 'Type', cell: (r) => r.leave_type },
                      { key: 'range', header: 'Range', cell: (r) => `${formatDate(r.start_date)} → ${formatDate(r.end_date)}` },
                      { key: 'status', header: 'Status', cell: (r) => statusBadge(r.status) },
                      { key: 'note', header: 'Reviewer note', cell: (r) => r.reviewer_note ?? '—', className: 'text-muted-foreground' },
                      { key: 'actions', header: '', cell: (r) => r.status === 'pending'
                        ? <Button size="sm" variant="ghost" className="rounded-full h-8" onClick={() => cancel(r.id)}>Cancel</Button>
                        : null },
                    ]}
                    emptyState={<EmptyState icon={<CalendarOff />} title="No requests yet" description="Click Request leave to submit your first." />}
                  />
                </div>
              </section>
            )}
          </TabsContent>

          {canSeeInbox && (
            <TabsContent value="types">
              <LeaveTypesPanel />
            </TabsContent>
          )}
        </Tabs>
      </div>

      <RequestLeaveDialog open={open} onOpenChange={setOpen} onSubmitted={() => { setOpen(false); myQ.reload(); }} />
      {canSeeInbox && (
        <RequestLeaveDialog
          open={logOpen}
          onOpenChange={setLogOpen}
          employees={empQ.data?.employees ?? []}
          onSubmitted={() => { setLogOpen(false); inboxQ.reload(); allQ.reload(); }}
        />
      )}
    </div>
  );
}

function statusBadge(s: LeaveRequest['status']): React.ReactNode {
  const tone: StatusTone =
    s === 'approved'  ? 'success' :
    s === 'pending'   ? 'info'    :
    s === 'denied'    ? 'destructive' :
    'muted';
  const label = s.charAt(0).toUpperCase() + s.slice(1);
  return (
    <span className={statusPill(tone)}>
      <span className={statusDot(tone)} />
      {label}
    </span>
  );
}
